// Shared background-blur helpers. MediaPipe's Selfie Segmentation is loaded
// once from jsDelivr the first time either the pre-lesson preview or the
// LiveKit publish path needs it, then reused.

const MEDIAPIPE_VERSION = '0.1.1675465747';
const MEDIAPIPE_BASE = `https://cdn.jsdelivr.net/npm/@mediapipe/selfie_segmentation@${MEDIAPIPE_VERSION}`;

let scriptPromise = null;

export function loadSelfieSegmentation() {
  if (typeof window === 'undefined') return Promise.reject(new Error('no window'));
  if (window.SelfieSegmentation) return Promise.resolve(window.SelfieSegmentation);
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `${MEDIAPIPE_BASE}/selfie_segmentation.js`;
    script.crossOrigin = 'anonymous';
    script.onload = () => {
      if (window.SelfieSegmentation) resolve(window.SelfieSegmentation);
      else reject(new Error('SelfieSegmentation missing after script load'));
    };
    script.onerror = () => {
      scriptPromise = null;
      reject(new Error('Failed to load MediaPipe SelfieSegmentation'));
    };
    document.head.appendChild(script);
  });
  return scriptPromise;
}

export function locateSelfieAsset(file) {
  return `${MEDIAPIPE_BASE}/${file}`;
}

// Safari didn't support CanvasRenderingContext2D.filter until v18. In older
// Safari, `ctx.filter = 'blur(12px)'` is silently ignored — the "blurred
// background" step draws a sharp frame, so the whole composite looks unblurred.
// Detect once so we know whether to use the fast native path or fall back to
// the downscale-upscale poor-man's blur.
let _ctxFilterSupported = null;
function ctxFilterSupported() {
  if (_ctxFilterSupported !== null) return _ctxFilterSupported;
  try {
    const c = document.createElement('canvas');
    c.width = 1; c.height = 1;
    const ctx = c.getContext('2d');
    if (!ctx || !('filter' in ctx)) { _ctxFilterSupported = false; return false; }
    ctx.filter = 'blur(2px)';
    _ctxFilterSupported = ctx.filter === 'blur(2px)';
  } catch (_) {
    _ctxFilterSupported = false;
  }
  return _ctxFilterSupported;
}

// Segments frames from videoEl and draws (blurred bg + sharp person) into
// canvasEl on every animation frame. Returns { stop } for cleanup.
// onFirstFrame fires the first time a real composite lands on the canvas —
// callers use it to swap the visible layer from the raw video to this canvas
// without a black flash while MediaPipe is still warming up.
export async function startBackgroundBlurLoop({ videoEl, canvasEl, blurPx = 12, onFirstFrame }) {
  const SelfieSegmentation = await loadSelfieSegmentation();

  let cancelled = false;
  let raf = 0;
  let firstFrameDelivered = false;
  const blurFilter = `blur(${blurPx}px)`;
  const nativeFilter = ctxFilterSupported();
  // Offscreen canvas used only in the fallback path (Safari < 18). Drawing
  // the source frame into a tiny canvas and then scaling it back up gives a
  // cheap gaussian-ish blur that ships everywhere.
  const smallCanvas = nativeFilter ? null : document.createElement('canvas');

  const segmenter = new SelfieSegmentation({ locateFile: locateSelfieAsset });
  segmenter.setOptions({ modelSelection: 1 });
  segmenter.onResults((results) => {
    if (cancelled) return;
    const ctx = canvasEl.getContext('2d');
    if (!ctx) return;
    const w = canvasEl.width;
    const h = canvasEl.height;
    if (w === 0 || h === 0) return;
    ctx.save();
    ctx.clearRect(0, 0, w, h);
    // 1. Draw mask (white where person is)
    ctx.drawImage(results.segmentationMask, 0, 0, w, h);
    // 2. Keep only person pixels from the sharp source frame
    ctx.globalCompositeOperation = 'source-in';
    ctx.drawImage(results.image, 0, 0, w, h);
    // 3. Draw blurred source frame behind the person
    ctx.globalCompositeOperation = 'destination-over';
    if (nativeFilter) {
      ctx.filter = blurFilter;
      ctx.drawImage(results.image, 0, 0, w, h);
      ctx.filter = 'none';
    } else {
      // Downscale to ~1/blurPx, then upscale with smoothing enabled. This
      // approximates a gaussian blur without needing ctx.filter support.
      const sw = Math.max(2, Math.round(w / Math.max(1, blurPx)));
      const sh = Math.max(2, Math.round(h / Math.max(1, blurPx)));
      if (smallCanvas.width !== sw) smallCanvas.width = sw;
      if (smallCanvas.height !== sh) smallCanvas.height = sh;
      const sCtx = smallCanvas.getContext('2d');
      sCtx.drawImage(results.image, 0, 0, sw, sh);
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(smallCanvas, 0, 0, w, h);
    }
    ctx.restore();
    if (!firstFrameDelivered) {
      firstFrameDelivered = true;
      if (onFirstFrame) {
        try { onFirstFrame(); } catch (_) {}
      }
    }
  });

  const process = async () => {
    if (cancelled) return;
    if (videoEl.readyState >= 2 && videoEl.videoWidth > 0) {
      if (canvasEl.width !== videoEl.videoWidth) canvasEl.width = videoEl.videoWidth;
      if (canvasEl.height !== videoEl.videoHeight) canvasEl.height = videoEl.videoHeight;
      try { await segmenter.send({ image: videoEl }); } catch (_) { /* keep loop alive */ }
    }
    if (!cancelled) raf = requestAnimationFrame(process);
  };
  raf = requestAnimationFrame(process);

  return {
    stop() {
      cancelled = true;
      if (raf) cancelAnimationFrame(raf);
      try { segmenter.close(); } catch (_) {}
    },
  };
}

// Wraps a raw camera MediaStreamTrack in a segmentation pipeline and returns
// a new MediaStreamTrack (from canvas.captureStream) that carries the blurred
// output. Used by the LiveKit publish path so the outgoing track shown to
// peers is already blurred. Caller must invoke dispose() on cleanup.
export async function createBlurredVideoTrack(sourceTrack, { blurPx = 12, fps = 30 } = {}) {
  if (!sourceTrack) return null;

  const videoEl = document.createElement('video');
  videoEl.autoplay = true;
  videoEl.playsInline = true;
  videoEl.muted = true;
  videoEl.srcObject = new MediaStream([sourceTrack]);
  // play() can reject if the tab is background, but the video will still
  // decode frames once foregrounded — swallow to keep the pipeline alive.
  try { await videoEl.play(); } catch (_) {}

  await new Promise((resolve) => {
    if (videoEl.readyState >= 2 && videoEl.videoWidth > 0) return resolve();
    const check = () => {
      if (videoEl.readyState >= 2 && videoEl.videoWidth > 0) {
        videoEl.removeEventListener('loadeddata', check);
        videoEl.removeEventListener('loadedmetadata', check);
        resolve();
      }
    };
    videoEl.addEventListener('loadeddata', check);
    videoEl.addEventListener('loadedmetadata', check);
  });

  const canvasEl = document.createElement('canvas');
  canvasEl.width = videoEl.videoWidth || 1280;
  canvasEl.height = videoEl.videoHeight || 720;

  const loop = await startBackgroundBlurLoop({ videoEl, canvasEl, blurPx });

  const outStream = canvasEl.captureStream(fps);
  const outTrack = outStream.getVideoTracks()[0];

  return {
    track: outTrack,
    dispose: () => {
      try { loop.stop(); } catch (_) {}
      try { outStream.getTracks().forEach(t => t.stop()); } catch (_) {}
      try {
        videoEl.pause();
        videoEl.srcObject = null;
      } catch (_) {}
    },
  };
}
